import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RefreshCw, Save, Sliders, ShieldAlert, LineChart } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/fishing")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "reviewSubmissions")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Fishing Forecast Admin — Coffman Cove Lodging" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: FishingAdmin,
});

type WeightRow = {
  id: string;
  species: string;
  factor: string;
  weight: number;
  active: boolean;
};

type RegulationRow = {
  id: string;
  species: string;
  status: "open" | "closed" | "restricted";
  source: string | null;
  effective_date: string;
  admin_override: boolean;
  note: string | null;
};

type ScoreRow = {
  id: string;
  zone_id: string;
  species: string;
  score_date: string;
  day_part: string;
  score: number;
};

type CatchRow = {
  id: string;
  zone_id: string | null;
  species: string;
  count: number;
  caught_at: string;
};

type ZoneRow = { id: string; name: string; slug: string };

const DAY_MS = 24 * 60 * 60 * 1000;

function FishingAdmin() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [weights, setWeights] = useState<WeightRow[]>([]);
  const [regulations, setRegulations] = useState<RegulationRow[]>([]);
  const [scores, setScores] = useState<ScoreRow[]>([]);
  const [catches, setCatches] = useState<CatchRow[]>([]);
  const [zones, setZones] = useState<ZoneRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userRes.user?.id ?? "");
      const admin = !!roles?.some((r) => r.role === "admin");
      setIsAdmin(admin);
      if (!admin) return;

      const since = new Date(Date.now() - 30 * DAY_MS).toISOString();

      const [w, r, s, c, z] = await Promise.all([
        supabase.from("scoring_weights").select("*").order("species").order("factor"),
        supabase.from("regulation_status").select("*").order("species"),
        supabase
          .from("fishing_scores")
          .select("id, zone_id, species, score_date, day_part, score")
          .gte("score_date", since.slice(0, 10))
          .order("score_date", { ascending: false }),
        supabase
          .from("guest_catches")
          .select("id, zone_id, species, count, caught_at")
          .gte("caught_at", since),
        supabase.from("fishing_zones").select("id, name, slug").order("name"),
      ]);

      setWeights((w.data ?? []).map((row) => ({ ...row, weight: Number(row.weight) })));
      setRegulations((r.data ?? []) as RegulationRow[]);
      setScores((s.data ?? []).map((row) => ({ ...row, score: Number(row.score) })));
      setCatches((c.data ?? []) as CatchRow[]);
      setZones((z.data ?? []) as ZoneRow[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const zoneName = useCallback(
    (id: string | null) => zones.find((z) => z.id === id)?.name ?? "Unassigned",
    [zones],
  );

  async function saveWeight(row: WeightRow) {
    setSavingId(row.id);
    const { error } = await supabase
      .from("scoring_weights")
      .update({ weight: row.weight, active: row.active })
      .eq("id", row.id);
    setSavingId(null);
    if (error) toast.error(`Could not save ${row.species}/${row.factor}: ${error.message}`);
    else toast.success(`Saved ${row.species} · ${row.factor.replace(/_/g, " ")}`);
  }

  async function saveRegulation(row: RegulationRow) {
    setSavingId(row.id);
    const { error } = await supabase
      .from("regulation_status")
      .update({
        status: row.status,
        note: row.note,
        admin_override: row.admin_override,
        effective_date: row.effective_date,
      })
      .eq("id", row.id);
    setSavingId(null);
    if (error) toast.error(`Could not save ${row.species}: ${error.message}`);
    else toast.success(`Saved ${row.species} regulation status`);
  }

  // Last 30 days: average score per zone/species against fish actually landed.
  const performance = useMemo(() => {
    const map = new Map<
      string,
      { zone: string; species: string; total: number; n: number; best: number; caught: number }
    >();

    for (const s of scores) {
      const key = `${s.zone_id}:${s.species}`;
      const entry =
        map.get(key) ??
        { zone: zoneName(s.zone_id), species: s.species, total: 0, n: 0, best: 0, caught: 0 };
      entry.total += s.score;
      entry.n += 1;
      entry.best = Math.max(entry.best, s.score);
      map.set(key, entry);
    }

    for (const c of catches) {
      const key = `${c.zone_id}:${c.species}`;
      const entry =
        map.get(key) ??
        { zone: zoneName(c.zone_id), species: c.species, total: 0, n: 0, best: 0, caught: 0 };
      entry.caught += c.count;
      map.set(key, entry);
    }

    return [...map.values()]
      .map((e) => ({ ...e, avg: e.n ? Math.round((e.total / e.n) * 10) / 10 : null }))
      .sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1));
  }, [scores, catches, zoneName]);

  const speciesGroups = useMemo(() => {
    const groups = new Map<string, WeightRow[]>();
    for (const w of weights) {
      groups.set(w.species, [...(groups.get(w.species) ?? []), w]);
    }
    return [...groups.entries()];
  }, [weights]);

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fishing forecast admin</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Scoring weights, regulation gates, and 30 days of scores against logged catches.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {isAdmin === false && (
        <div className="mt-10 rounded-lg border border-border bg-card p-6 text-sm">
          Your account is signed in but does not have admin access. Contact the site owner to
          be added.
        </div>
      )}

      {isAdmin && (
        <div className="mt-8 space-y-10">
          {/* ---------------- scoring weights ---------------- */}
          <section>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Sliders className="h-4 w-4" /> Scoring weights
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Weights are normalised at scoring time, so relative size is what matters.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {speciesGroups.map(([species, rows]) => (
                <div key={species} className="rounded-lg border border-border bg-card p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider">{species}</h3>
                  <ul className="mt-3 space-y-2">
                    {rows.map((row) => (
                      <li key={row.id} className="flex items-center gap-2">
                        <span className="flex-1 text-sm">{row.factor.replace(/_/g, " ")}</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          value={row.weight}
                          onChange={(e) =>
                            setWeights((prev) =>
                              prev.map((w) =>
                                w.id === row.id ? { ...w, weight: Number(e.target.value) } : w,
                              ),
                            )
                          }
                          className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm"
                          aria-label={`${species} ${row.factor} weight`}
                        />
                        <label className="flex items-center gap-1 text-xs text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={row.active}
                            onChange={(e) =>
                              setWeights((prev) =>
                                prev.map((w) =>
                                  w.id === row.id ? { ...w, active: e.target.checked } : w,
                                ),
                              )
                            }
                          />
                          on
                        </label>
                        <button
                          type="button"
                          onClick={() => void saveWeight(row)}
                          disabled={savingId === row.id}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                        >
                          <Save className="h-3 w-3" /> Save
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* ---------------- regulation status ---------------- */}
          <section>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <ShieldAlert className="h-4 w-4" /> Regulation status
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              A species marked closed is hard-gated to a score of zero and never recommended.
            </p>

            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2">Species</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Effective</th>
                    <th className="px-3 py-2">Note</th>
                    <th className="px-3 py-2">Override</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {regulations.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{row.species}</td>
                      <td className="px-3 py-2">
                        <select
                          value={row.status}
                          onChange={(e) =>
                            setRegulations((prev) =>
                              prev.map((r) =>
                                r.id === row.id
                                  ? { ...r, status: e.target.value as RegulationRow["status"] }
                                  : r,
                              ),
                            )
                          }
                          className="rounded-md border border-input bg-background px-2 py-1"
                          aria-label={`${row.species} status`}
                        >
                          <option value="open">open</option>
                          <option value="restricted">restricted</option>
                          <option value="closed">closed</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="date"
                          value={row.effective_date}
                          onChange={(e) =>
                            setRegulations((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, effective_date: e.target.value } : r,
                              ),
                            )
                          }
                          className="rounded-md border border-input bg-background px-2 py-1"
                          aria-label={`${row.species} effective date`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={row.note ?? ""}
                          onChange={(e) =>
                            setRegulations((prev) =>
                              prev.map((r) =>
                                r.id === row.id ? { ...r, note: e.target.value } : r,
                              ),
                            )
                          }
                          className="w-full min-w-48 rounded-md border border-input bg-background px-2 py-1"
                          aria-label={`${row.species} note`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={row.admin_override}
                          onChange={(e) =>
                            setRegulations((prev) =>
                              prev.map((r) =>
                                r.id === row.id
                                  ? { ...r, admin_override: e.target.checked }
                                  : r,
                              ),
                            )
                          }
                          aria-label={`${row.species} admin override`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => void saveRegulation(row)}
                          disabled={savingId === row.id}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                        >
                          <Save className="h-3 w-3" /> Save
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ---------------- scores vs catches ---------------- */}
          <section>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <LineChart className="h-4 w-4" /> Last 30 days: scores vs logged catches
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {scores.length} score rows and {catches.reduce((a, c) => a + c.count, 0)} logged
              fish across {performance.length} zone/species pairs.
            </p>

            <div className="mt-4 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2">Zone</th>
                    <th className="px-3 py-2">Species</th>
                    <th className="px-3 py-2">Avg score</th>
                    <th className="px-3 py-2">Best</th>
                    <th className="px-3 py-2">Scored windows</th>
                    <th className="px-3 py-2">Fish landed</th>
                  </tr>
                </thead>
                <tbody>
                  {performance.map((row) => (
                    <tr key={`${row.zone}-${row.species}`} className="border-t border-border">
                      <td className="px-3 py-2">{row.zone}</td>
                      <td className="px-3 py-2">{row.species}</td>
                      <td className="px-3 py-2 font-medium">{row.avg ?? "—"}</td>
                      <td className="px-3 py-2">{row.n ? row.best : "—"}</td>
                      <td className="px-3 py-2">{row.n}</td>
                      <td className="px-3 py-2">{row.caught}</td>
                    </tr>
                  ))}
                  {!performance.length && (
                    <tr>
                      <td className="px-3 py-6 text-muted-foreground" colSpan={6}>
                        No scores computed in the last 30 days yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
